import { useEffect, useRef } from "react"
import { Api } from "../api"

type Socket = any

type Args = {
  socket: Socket | null
  orderId: string
  eventId: string
  active: boolean
  onApproved: (payload: any) => void
  onRejected: (payload: any) => void
  onCanceled?: (payload: any) => void
  socketTimeoutMs?: number
  pollIntervalMs?: number
}

export function usePaymentListener({
  socket,
  orderId,
  eventId,
  active,
  onApproved,
  onRejected,
  onCanceled,
  socketTimeoutMs = 45000,
  pollIntervalMs = 4000,
}: Args) {
  const terminalRef = useRef(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (!active) return

    const handleSocket = (payload: any) => {
      if (terminalRef.current) return
      if (payload?.paymentStatus === "approved") {
        terminalRef.current = true
        onApproved(payload)
      } else if (payload?.paymentStatus === "rejected") {
        terminalRef.current = true
        onRejected(payload)
      }
    }

    const handleCanceled = (payload: any) => {
      if (terminalRef.current) return
      terminalRef.current = true
      if (onCanceled) onCanceled(payload)
      else
        onRejected({
          ...payload,
          statusDetail: payload?.reason ?? "order_canceled",
        })
    }

    socket?.on("orderUpdate", handleSocket)
    socket?.on("orderCanceled", handleCanceled)

    timeoutRef.current = setTimeout(() => {
      if (terminalRef.current) return
      pollRef.current = setInterval(async () => {
        if (terminalRef.current) return
        const res = await Api.get.purchaseInfo({ eventId, orderId })
        if (!res.ok) return
        const payments = (res.data as any)?.payments ?? []
        if (payments.length === 0) return
        const terminal = payments[0].transition_code !== null
        if (!terminal) return
        terminalRef.current = true
        const approved =
          payments[0].status === "approved" ||
          payments[0].transition_code === "APPROVED"
        if (approved) onApproved(res.data)
        else onRejected(res.data)
      }, pollIntervalMs)
    }, socketTimeoutMs)

    return () => {
      socket?.off("orderUpdate", handleSocket)
      socket?.off("orderCanceled", handleCanceled)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      if (pollRef.current) clearInterval(pollRef.current)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, orderId, eventId, active])
}
